import { useCallback } from 'react';
import { trackExperimentTurnOnFlagButtonClicked } from '@gonfalon/experiments/analytics';
import type { Experiment } from '@gonfalon/experiments/types';
import { msg } from '@gonfalon/messages/msg';
import { useUpdateFlagSemantically } from '@gonfalon/rest-api/hooks/useUpdateFlagSemantically';
import { useUpdateAiConfigTargeting } from '@gonfalon/rest-api/queries/aiConfigs';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { snackbarQueue } from '@launchpad-ui/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type UseToggleExperimentFlagOnArgs = {
  projectKey: string;
  environmentKey: string;
  flagKey: string;
  isAiConfig: boolean;
  component: string;
  experiment?: Experiment;
};

/**
 * Turns on the flag (or AI config) backing an experiment. Flags use the `turnFlagOn`
 * semantic patch; AI configs must go through their own targeting endpoint with `enableTargeting`.
 *
 * The env-scoped flag query is invalidated before the mutation settles, so callers can derive
 * the flag's `on` state from that query instead of tracking it themselves.
 */
export function useToggleExperimentFlagOn({
  projectKey,
  environmentKey,
  flagKey,
  isAiConfig,
  component,
  experiment,
}: UseToggleExperimentFlagOnArgs) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateFlagSemantically } = useUpdateFlagSemantically();
  const { mutateAsync: updateAiConfigTargeting } = useUpdateAiConfigTargeting();

  const {
    mutateAsync,
    isPending: isToggling,
    isSuccess: hasToggledOn,
  } = useMutation({
    mutationFn: async () => {
      if (isAiConfig) {
        await updateAiConfigTargeting({
          projectKey,
          configKey: flagKey,
          body: {
            environmentKey,
            instructions: [{ kind: 'enableTargeting' }],
          },
        });
      } else {
        await updateFlagSemantically({
          projectKey,
          featureFlagKey: flagKey,
          body: {
            environmentKey,
            instructions: [{ kind: 'turnFlagOn' }],
          },
        });
      }
      // the mutation only updates the env-less cache entry; refresh the env-scoped one callers read `on` from
      await queryClient.invalidateQueries({
        queryKey: getFeatureFlagQuery({ projectKey, featureFlagKey: flagKey, query: { env: [environmentKey] } })
          .queryKey,
      });
    },
    onError: () => {
      snackbarQueue.add({
        description: msg('experiments.turnOnFlagToast.error', { isAiConfig: isAiConfig ? 'true' : 'false' }),
        status: 'error',
      });
    },
  });

  const toggleOn = useCallback(async () => {
    trackExperimentTurnOnFlagButtonClicked({
      experimentId: experiment?._id,
      experimentKey: experiment?.key,
      flagKey,
      projectKey,
      environmentKey,
      type: isAiConfig ? 'AI Config' : 'Flag',
      component,
    });
    // failures surface through the onError snackbar
    await mutateAsync().catch(() => {});
  }, [experiment?._id, experiment?.key, flagKey, projectKey, environmentKey, isAiConfig, component, mutateAsync]);

  return { toggleOn, isToggling, hasToggledOn };
}
